import { useTranslation } from "react-i18next";
import {
  AD_TYPES,
  AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES,
  type AudienceSegmentType,
} from "../../../components/core/blocks/AdPlan/constants";
import { css } from "../../../../styled-system/css";
import { Wrap } from "../../../../styled-system/jsx";
import { vstack } from "../../../../styled-system/patterns";
import { NumberInput } from "../../../components/fields/base/NumberInput";
import { useAppSelector } from "../../../redux/hooks";
import AudienceMultiplierField from "../../../components/core/blocks/AdPlan/fields/AudienceMultiplierField";
import { Select } from "../../../components/fields/base/Select";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { useResolvedLabels } from "../../../i18n/useResolvedLabels";

export interface BulkAdPlanModifierFormData {
  topOfSearchModifier: number;
  productPagesModifier: number;
  restOfSearchModifier: number;
  amazonBusinessModifier: number;
  audienceBidMultiplierTargetAudienceId?: number | null;
  audienceBidMultiplierTargetAudienceName: string | null;
  audienceBidMultiplierTargetAudienceSegmentType: AudienceSegmentType | null;
  audienceBidMultiplier?: number;
  sbOtherPageModifier: number;
  sbProductDetailPageModifier: number;
  sbHomePageModifier: number;
  adType: number;
  updatePlacementModifiers: boolean;
  updateAudienceModifiers: boolean;
  algoManagedPlacementMultipliers: boolean;
}

export const INITIAL_MODIFIER_FORM_DATA: BulkAdPlanModifierFormData = {
  topOfSearchModifier: 0,
  productPagesModifier: 0,
  restOfSearchModifier: 0,
  amazonBusinessModifier: 0,
  audienceBidMultiplierTargetAudienceId: null,
  audienceBidMultiplierTargetAudienceName: null,
  audienceBidMultiplierTargetAudienceSegmentType: null,
  sbProductDetailPageModifier: 0,
  sbOtherPageModifier: 0,
  sbHomePageModifier: 0,
  audienceBidMultiplier: 0,
  adType: AD_TYPES.SPONSORED_PRODUCT.value,
  updatePlacementModifiers: true,
  updateAudienceModifiers: false,
  algoManagedPlacementMultipliers: false,
};

interface Props {
  formData: BulkAdPlanModifierFormData;
  setFormData: (data: BulkAdPlanModifierFormData) => void;
}

export const keysToIngoreModifierValidationsFor: (keyof BulkAdPlanModifierFormData)[] = [
  "audienceBidMultiplierTargetAudienceId",
  "audienceBidMultiplierTargetAudienceName",
  "audienceBidMultiplierTargetAudienceSegmentType",
  "updateAudienceModifiers",
  "updatePlacementModifiers",
  "algoManagedPlacementMultipliers",
];

const BulkAdPlanModifierForm = ({ formData, setFormData }: Props) => {
  const { t } = useTranslation(["advertising"]);
  const { marketPlace } = useAppSelector((state) => state.marketPlace);

  const adTypeOptions = [AD_TYPES.SPONSORED_PRODUCT, AD_TYPES.SPONSORED_PRODUCT_AUTO, AD_TYPES.SPONSORED_BRAND_V2];
  const resolvedAdTypeOptions = useResolvedLabels(adTypeOptions, ["advertising"]);
  const handleChange = <T extends keyof BulkAdPlanModifierFormData>(key: T, value: BulkAdPlanModifierFormData[T]) => {
    setFormData({ ...formData, [key]: value });
  };

  const filteredValues = Object.entries(formData)
    .filter(([key]) => !keysToIngoreModifierValidationsFor.includes(key))
    .map(([, value]) => value);
  const hasInvalidValue = filteredValues.some((value) => isNaN(parseInt(value as string)) || value > 900 || value < 0);

  const adTypeLabelSelector = Object.values(AD_TYPES).find((adType) => adType.value === formData.adType)?.label;
  const adTypeLabel = adTypeLabelSelector ? t(adTypeLabelSelector) : "";

  return (
    <form
      className={vstack({
        alignItems: "stretch",
        width: "100%",
        paddingX: "xlarge",
        overflow: "visible",
      })}
    >
      <p className={css({ textAlign: "center" })}>
        {t(
          ($) =>
            $.advertising[
              "Please specify changes to your placement modifiers using the fields below. Modifiers will be updated for all selected media plans of the specified type."
            ],
        )}
      </p>
      <Wrap gap="xlarge" width="50%">
        <Select
          label={t(($) => $.advertising["Media Plan Type"])}
          onSelectionChange={(value) => {
            handleChange("adType", value as number);
          }}
          selectedKey={formData.adType}
          items={resolvedAdTypeOptions}
        />
      </Wrap>
      <h4 className={css({ display: "flex", gap: "small", fontSize: "medium", fontWeight: "bolder" })}>
        {t(($) => $.advertising["Update {adTypeLabel} Placement Modifiers"], { adTypeLabel })}{" "}
        <Checkbox
          value={formData.updatePlacementModifiers}
          onChange={(checked) => handleChange("updatePlacementModifiers", checked)}
        />
      </h4>
      <Wrap hidden={!formData.updatePlacementModifiers} gap="xlarge" width={"100%"}>
        {[AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(formData.adType) ?
          <>
            <NumberInput
              onChange={(value) => handleChange("topOfSearchModifier", value)}
              value={formData.topOfSearchModifier}
              label={t(($) => $.advertising["Top of Search"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("productPagesModifier", value)}
              value={formData.productPagesModifier}
              label={t(($) => $.advertising["Product Pages"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("restOfSearchModifier", value)}
              value={formData.restOfSearchModifier}
              label={t(($) => $.advertising["Rest of Search"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
          </>
        : [AD_TYPES.SPONSORED_BRAND_V2.value].includes(formData.adType) ?
          <>
            <NumberInput
              onChange={(value) => handleChange("sbHomePageModifier", value)}
              value={formData.sbHomePageModifier}
              label={t(($) => $.advertising["Amazon Home Page"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("sbProductDetailPageModifier", value)}
              value={formData.sbProductDetailPageModifier}
              label={t(($) => $.advertising["Product Detail Page"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => handleChange("sbOtherPageModifier", value)}
              value={formData.sbOtherPageModifier}
              label={t(($) => $.advertising["Other Amazon Pages"])}
              placeholder={t(($) => $.advertising["Enter amount"])}
              minValue={0}
            />
          </>
        : null}
        {(
          AMAZON_BUSINESS_MULTIPLIER_SUPPORT_MARKETPLACES.includes(marketPlace) &&
          [AD_TYPES.SPONSORED_PRODUCT.value, AD_TYPES.SPONSORED_PRODUCT_AUTO.value].includes(formData.adType)
        ) ?
          <NumberInput
            onChange={(value) => handleChange("amazonBusinessModifier", value)}
            value={formData.amazonBusinessModifier}
            label={t(($) => $.advertising["Amazon Business"])}
            placeholder={t(($) => $.advertising["Enter amount"])}
            isDisabled={!formData.updatePlacementModifiers}
            minValue={0}
          />
        : null}
      </Wrap>

      <h4 className={css({ display: "flex", gap: "small", fontSize: "medium", fontWeight: "bolder" })}>
        {t(($) => $.advertising["Update {adTypeLabel} Audience Modifiers"], { adTypeLabel })}
        <Checkbox
          value={formData.updateAudienceModifiers}
          onChange={(checked) => handleChange("updateAudienceModifiers", checked)}
        />
      </h4>
      <Wrap className={css({ zIndex: 2000 })} hidden={!formData.updateAudienceModifiers} gap="xlarge" width={"50%"}>
        <AudienceMultiplierField
          state={{ ...formData, adType: formData.adType }}
          onChange={(updates: {
            audienceBidMultiplierTargetAudienceId?: number | null;
            audienceBidMultiplierTargetAudienceName: string | null;
            audienceBidMultiplierTargetAudienceSegmentType: AudienceSegmentType | null;
            amazonBusinessModifier?: number;
          }) => {
            setFormData({ ...formData, ...updates });
          }}
          errors={[]}
          fieldsToValidate={[]}
          hideTitle={true}
          bulkActionForm={true}
          disableFields={!formData.updateAudienceModifiers}
          flexDirection="row"
        />
      </Wrap>

      {hasInvalidValue ?
        <p className={css({ paddingTop: "medium", color: "red", fontSize: "small", textAlign: "center" })}>
          {t(($) => $.advertising["All modifiers must have a value between 0 and 900"])}
        </p>
      : null}
    </form>
  );
};

export default BulkAdPlanModifierForm;
